import axios from "../config/axios";
import { authenticatedFetch } from "../util/authenticatedFetch";
import { downloadBlob } from "../utils/downloadBlob";
import { contentDispositionFileName } from "../utils/responseHeaders";

/**
 * Whether `url` is an ai_engine route on the API origin (`/api/ai/*`), the one kind of file URL
 * that needs the tab's credentials. The browser sends such a request with the cookie alone, so
 * without the shared transport it reaches the login's own company, not the tab's project (a
 * staff tab acting on another company's project gets 403). Its bytes are point-checked on every
 * read and never enter a durable cache. Anything else (a signed blob link, an object URL) carries
 * its own authority and never receives a token.
 */
export function isFirstPartyApiUrl(url: string): boolean {
    try {
        const base = axios.defaults?.baseURL ?? window.location.origin;
        const target = new URL(url, base);
        return target.origin === new URL(base).origin && target.pathname.startsWith("/api/ai/");
    } catch {
        return false;
    }
}

/**
 * Reads `url`'s bytes: an `/api/ai` URL through the shared transport (the tab's company session
 * token and project headers, never the cookie alone), anything else (a signed blob link, an object
 * URL) as it is, with no credentials. The one way to fetch a URL a caller was handed.
 */
export async function fetchAuthorizedBytes(url: string, signal?: AbortSignal): Promise<Response> {
    if (!isFirstPartyApiUrl(url)) return fetch(url, { mode: "cors", signal });
    const target = new URL(url, axios.defaults.baseURL ?? window.location.origin);
    // PP-827 extends this shared transport with canonical project/company
    // headers and placement cancellation. Do not fork its admission logic.
    return authenticatedFetch(target, {
        signal,
        cache: "no-store",
    });
}

/** A download the server refused, with the sentence to show. */
export class AuthorizedDownloadError extends Error {
    readonly status: number;
    constructor(status: number) {
        super(
            status === 403
                ? "You do not have permission to download this file."
                : "Unable to download this file. Try again.",
        );
        this.name = "AuthorizedDownloadError";
        this.status = status;
    }
}

/**
 * Saves `url` as a file: fetched through {@link fetchAuthorizedBytes}, then handed to the browser
 * as an object URL. The one way to download a URL; an `<a href>` (or a navigation) to an `/api/ai`
 * URL carries the cookie alone and lands in the login's own company. Without `fileName` the file
 * takes the server's `Content-Disposition` name. Throws {@link AuthorizedDownloadError} on a
 * refusal, so an error body is never saved as the file.
 */
export async function downloadAuthorizedFile(
    url: string,
    fileName?: string,
    signal?: AbortSignal,
): Promise<void> {
    const response = await fetchAuthorizedBytes(url, signal);
    if (!response.ok) throw new AuthorizedDownloadError(response.status);
    const blob = await response.blob();
    downloadBlob(
        blob,
        fileName ??
            contentDispositionFileName(response.headers.get("Content-Disposition") ?? undefined) ??
            "download",
    );
}
